import { useMemo, useState, useEffect, useCallback } from "react";
import styles from "./ConversionAndPdpViewer.module.css";
import { useAuthUser } from "../../contexts/AuthUserContext";
import {
  fetchConversionAndPdpWindows,
  generateConversionAndPdpReport,
  getPdpDeepDiveAnalysis,
  analyzePdpDeepDive as analyzePdpDeepDiveApi,
  getProductGscPerformance,
} from "../../utils/api/reportApi";
import {
  useAnalysisFlow,
  AnalysisFlowLog,
} from "./helpers/metaReportViewer/AnalysisFlowLog";
import {
  buildWindowEntry,
  buildWindowKey,
} from "./helpers/conversionAndPdpViewer/formatters";
import {
  attachComparisonTrends,
  buildProductMatchKeys,
  createProductLookup,
  resolveProductRecord,
  resolveWindowPayload,
} from "./helpers/conversionAndPdpViewer/normalize";
import { MainTabs } from "./helpers/conversionAndPdpViewer/ui";
import WebsiteConversionTab from "./helpers/conversionAndPdpViewer/WebsiteConversionTab";
import StoreFunnelTab from "./helpers/conversionAndPdpViewer/StoreFunnelTab";
import PdpRankingsTab from "./helpers/conversionAndPdpViewer/PdpRankingsTab";
import PdpDeepDiveTab from "./helpers/conversionAndPdpViewer/PdpDeepDiveTab";

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hasTopSellingSignal = (row = {}) => {
  const orderCount = toFiniteNumber(
    row?.commercialOutcome?.purchaseCount ??
      row?.purchaseCount ??
      row?.orders ??
      0,
  );
  const conversionRate = toFiniteNumber(
    row?.conversionQuality?.pdpToPurchaseRate ??
      row?.purchaseToViewRate ??
      row?.pdpToPurchaseRate ??
      0,
  );
  return orderCount > 0 || conversionRate > 0;
};

export default function ConversionAndPdpViewer({
  data = {},
  report = {},
  onOpenChannel = null,
  onWindowChange = null,
}) {
  console.log("[ConversionAndPdpViewer] render", {
    reportId: report?._id || report?.id || null,
    userId: report?.user_id || null,
    shopId: report?.shop_id || null,
    collectionName: data?.collection_name || null,
    generationKey: data?.generation_key || null,
    hasAvailableWindows: Array.isArray(data?.available_windows) && data.available_windows.length > 0,
    windowCount: Array.isArray(data?.windows) ? data.windows.length : 0,
    defaultWindowKey: data?.default_window_key || null,
  });

  const { user } = useAuthUser();
  const authToken = user?.token || null;
  const userId = report?.user_id || null;
  const shopId = report?.shop_id || null;

  // Build initial window list from nested rollup data, falling back to legacy single-window
  const initialWindows = useMemo(() => {
    const nested =
      Array.isArray(data?.windows) && data.windows.length
          ? data.windows
          : [];
    if (nested.length) {
      return nested.map((w) =>
        buildWindowEntry({
          ...w,
          reportId: report?._id || report?.id || null,
          generationKey: data?.generation_key || null,
        })
      );
    }
    // Legacy single-window fallback
    const period = data?.reporting_period || {};
    return period?.startDate && period?.endDate
      ? [
          buildWindowEntry({
            startDate: period.startDate,
            endDate: period.endDate,
            reportId: report?._id || report?.id || null,
            generationKey: data?.generation_key || null,
          }),
        ]
      : [];
  }, [
    data?.available_windows,
    data?.windows,
    data?.reporting_period,
    data?.generation_key,
    report?._id,
    report?.id,
  ]);

  const [activeTab, setActiveTab] = useState("websiteConversion");
  const [activeRankingTab, setActiveRankingTab] = useState("topSelling");
  const [selectedProductSeed, setSelectedProductSeed] = useState(null);
  const [windows, setWindows] = useState(initialWindows);
  const [windowError, setWindowError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [fetchedPdpAnalysis, setFetchedPdpAnalysis] = useState(null);
  const [fetchedSeoData, setFetchedSeoData] = useState(null);
  const [fetchedGscData, setFetchedGscData] = useState(null);
  const [gscEnrichment, setGscEnrichment] = useState({});
  const [pdpAnalysisError, setPdpAnalysisError] = useState("");
  const [isAnalyzingPdp, setIsAnalyzingPdp] = useState(false);
  const [isCheckingPdpAnalysis, setIsCheckingPdpAnalysis] = useState(false);
  const pdpFlow = useAnalysisFlow();

  const defaultWindowKey =
    data?.default_window_key || initialWindows[0]?.key || null;
  const [activeWindowKey, setActiveWindowKey] = useState(defaultWindowKey);
  const [comparisonWindowKey, setComparisonWindowKey] = useState(
    initialWindows.find((w) => w.key !== defaultWindowKey)?.key || null
  );

  // Sync when the parent provides a new report payload
  useEffect(() => {
    setWindows(initialWindows);
    const nextDefault =
      data?.default_window_key || initialWindows[0]?.key || null;
    setActiveWindowKey((currentKey) => {
      if (initialWindows.some((w) => w.key === currentKey)) return currentKey;
      return nextDefault;
    });
    setComparisonWindowKey(
      initialWindows.find((w) => w.key !== nextDefault)?.key || null
    );
  }, [initialWindows, data?.default_window_key]);

  const handleWindowSelect = useCallback((nextWindowKey) => {
    setActiveWindowKey(nextWindowKey);
    const selectedWindow = windows.find((w) => String(w.key) === String(nextWindowKey));
    if (!selectedWindow || typeof onWindowChange !== "function") return;
    onWindowChange({
      key: selectedWindow.key || nextWindowKey,
      label: selectedWindow.label || nextWindowKey,
      days: selectedWindow.days || null,
      startDate: selectedWindow.startDate || null,
      endDate: selectedWindow.endDate || null,
      reportId: selectedWindow.reportId || report?._id || report?.id || null,
      generationKey: selectedWindow.generationKey || data?.generation_key || null,
      reportKey: "conversion",
      collection: "conversion_and_pdp_report",
      section: "assessment",
      windowKey: selectedWindow.key || nextWindowKey,
      assessmentKey: "conversion",
      assessmentLabel: "Conversion & PDP",
    });
  }, [data?.generation_key, onWindowChange, report?._id, report?.id, windows]);

  // Fetch window list only when the report itself has no embedded windows (legacy)
  useEffect(() => {
    if (initialWindows.length > 0) return;
    if (!authToken || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchConversionAndPdpWindows({
          token: authToken,
          userId,
          shopId,
        });
        if (cancelled) return;
        const fetched = Array.isArray(result?.windows)
          ? result.windows.map((w) => buildWindowEntry(w))
          : [];
        if (fetched.length) {
          setWindows(fetched);
          setActiveWindowKey((cur) => cur || fetched[0]?.key || null);
          setComparisonWindowKey((cur) =>
            cur || fetched.find((w) => w.key !== fetched[0]?.key)?.key || null
          );
        }
      } catch (err) {
        setWindowError(err?.message || "Failed to load available report windows");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken, userId, shopId, initialWindows.length]);

  // Derive active and comparison window payloads from the single rollup report
  const activeWindowData = useMemo(
    () => resolveWindowPayload(data, activeWindowKey),
    [data, activeWindowKey]
  );
  const comparisonWindowData = useMemo(
    () =>
      comparisonWindowKey && comparisonWindowKey !== activeWindowKey
        ? resolveWindowPayload(data, comparisonWindowKey)
        : null,
    [data, comparisonWindowKey, activeWindowKey]
  );

  const handleGenerate = useCallback(async () => {
    if (!userId) return;
    setGenerating(true);
    setWindowError(null);
    try {
      await generateConversionAndPdpReport({ userId, shopId, authToken });
    } catch (err) {
      setWindowError(err?.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [userId, shopId, authToken]);

  // Load cached deep dive analysis + SEO data whenever selected product changes
  useEffect(() => {
    setFetchedPdpAnalysis(null);
    setFetchedSeoData(null);
    setFetchedGscData(null);
    setPdpAnalysisError("");
    if (!authToken || !userId || !selectedProductSeed) return;
    let cancelled = false;
    setIsCheckingPdpAnalysis(true);
    (async () => {
      try {
        const result = await getPdpDeepDiveAnalysis({
          userId,
          reportId: report?._id || report?.id || "",
          shopId: shopId || "",
          itemId: selectedProductSeed?.itemId || "",
          itemName: selectedProductSeed?.itemName || selectedProductSeed?.title || "",
          shopifyProductId: selectedProductSeed?.shopifyProductId || "",
          authToken,
        });
        if (!cancelled) {
          setFetchedPdpAnalysis(result?.analysis || null);
          setFetchedSeoData(result?.seoData || null);
          setFetchedGscData(result?.gscData || null);
        }
      } catch {
        // no cached result — that's fine, show the Analyze button
      } finally {
        if (!cancelled) setIsCheckingPdpAnalysis(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    authToken,
    userId,
    shopId,
    report?._id,
    report?.id,
    selectedProductSeed?.itemId,
    selectedProductSeed?.itemName,
    selectedProductSeed?.title,
  ]);

  const handleAnalyzePdpDeepDive = useCallback(async () => {
    if (!authToken || !userId || !selectedProductSeed) return;
    setIsAnalyzingPdp(true);
    setPdpAnalysisError("");
    pdpFlow.start();
    try {
      const result = await analyzePdpDeepDiveApi({
        userId,
        reportId: report?._id || report?.id || "",
        shopId: shopId || "",
        itemId: selectedProductSeed?.itemId || "",
        itemName: selectedProductSeed?.itemName || selectedProductSeed?.title || "",
        authToken,
        force: true,
      });
      pdpFlow.resolve();
      setFetchedPdpAnalysis(result?.analysis || null);
    } catch (err) {
      pdpFlow.fail(err?.message || "Analysis failed");
      setPdpAnalysisError(err?.message || "Analysis failed");
    } finally {
      setIsAnalyzingPdp(false);
    }
  }, [authToken, userId, shopId, report?._id, report?.id, selectedProductSeed, pdpFlow]);

  // Enrich losingMomentum rows with GSC performance from product_seo_reports
  useEffect(() => {
    if (!authToken || !shopId) return;
    const losingRows = activeWindowData?.pdp_rankings?.losingMomentum || [];
    const missing = losingRows
      .filter((r) => !r.gscData && r.shopifyProductId)
      .map((r) => r.shopifyProductId);
    if (!missing.length) return;
    let cancelled = false;
    getProductGscPerformance({ shopId, productIds: missing, authToken })
      .then((data) => { if (!cancelled && data) setGscEnrichment((prev) => ({ ...prev, ...data })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authToken, shopId, activeWindowData]);

  // Support both data.pdp_deep_dive array shape and nested objects
  const deepDiveProducts = useMemo(() => {
    const arr =
      activeWindowData?.pdp_deep_dive ||
      activeWindowData?.pdpDeepDive?.products ||
      activeWindowData?.pdpDeepDive ||
      [];
    return Array.isArray(arr) ? arr : [];
  }, [activeWindowData]);

  const comparisonProducts = useMemo(() => {
    const rankingRoot =
      comparisonWindowData?.pdp_rankings ||
      comparisonWindowData?.pdpRankings ||
      {};
    const rankingRows = [
      ...(Array.isArray(rankingRoot?.topSelling) ? rankingRoot.topSelling : []),
      ...(Array.isArray(rankingRoot?.highestContribution)
        ? rankingRoot.highestContribution
        : []),
      ...(Array.isArray(rankingRoot?.mostOpportunity)
        ? rankingRoot.mostOpportunity
        : []),
      ...(Array.isArray(rankingRoot?.bestConverting)
        ? rankingRoot.bestConverting
        : []),
      ...(Array.isArray(rankingRoot?.losingMomentum)
        ? rankingRoot.losingMomentum
        : []),
    ];
    const deepDiveRows = Array.isArray(comparisonWindowData?.pdp_deep_dive)
      ? comparisonWindowData.pdp_deep_dive
      : [];
    return [...rankingRows, ...deepDiveRows];
  }, [comparisonWindowData]);

  const comparisonLookup = useMemo(
    () => createProductLookup(comparisonProducts),
    [comparisonProducts]
  );

  const deepDiveLookup = useMemo(
    () => createProductLookup(fetchedPdpAnalysis ? [fetchedPdpAnalysis] : deepDiveProducts),
    [fetchedPdpAnalysis, deepDiveProducts]
  );

  const narrativeLookup = useMemo(() => {
    const rows = Array.isArray(activeWindowData?.narrative?.pdpDeepDiveNarratives)
      ? activeWindowData.narrative.pdpDeepDiveNarratives
      : [];
    const map = new Map();
    rows.forEach((row) => {
      buildProductMatchKeys(row).forEach((key) => {
        if (!map.has(key)) map.set(key, row);
      });
    });
    return map;
  }, [activeWindowData]);

  const selectedProduct = useMemo(() => {
    if (!selectedProductSeed) return null;
    const resolved = resolveProductRecord({
      seedRecord: selectedProductSeed,
      deepDiveLookup,
      narrativeLookup,
    });
    return attachComparisonTrends({ record: resolved, comparisonLookup });
  }, [selectedProductSeed, deepDiveLookup, narrativeLookup, comparisonLookup]);

  const openProduct = (productRow) => {
    // Reset all deep dive state immediately so the previous product's analysis
    // is never shown against a newly opened product
    setFetchedPdpAnalysis(null);
    setFetchedSeoData(null);
    setPdpAnalysisError("");
    setIsCheckingPdpAnalysis(false);
    pdpFlow.reset();
    setSelectedProductSeed(productRow || null);
    setActiveTab("pdpDeepDive");
  };

  const shopCurrency =
    activeWindowData?.shop_currency || data?.shop_currency || "USD";

  const comparisonReportingPeriod =
    comparisonWindowData?.reporting_period || null;
  const comparisonLabel = comparisonReportingPeriod
    ? `${comparisonReportingPeriod.startDate} to ${comparisonReportingPeriod.endDate}`
    : null;

  const websiteConversionData = useMemo(() => {
    const m =
      activeWindowData?.website_overall_conversion ||
      activeWindowData?.websiteConversion ||
      {};
    const rawGa4Metrics =
      activeWindowData?.website_overall_conversion_raw_ga4 ||
      activeWindowData?.websiteOverallConversionRawGa4 ||
      m;
    const comparisonMetrics =
      comparisonWindowData?.website_overall_conversion ||
      comparisonWindowData?.websiteConversion ||
      {};
    const comparisonRawGa4Metrics =
      comparisonWindowData?.website_overall_conversion_raw_ga4 ||
      comparisonWindowData?.websiteOverallConversionRawGa4 ||
      comparisonMetrics;
    return {
      aiOverview: activeWindowData?.narrative?.websiteConversionOverview || null,
      comparisonLabel,
      headlineMetrics: {
        websiteSessions: m.websiteSessions,
        pdpViews: m.pdpViewSessions,
        addToCart: m.addToCartSessions,
        checkout: m.beginCheckoutSessions,
        purchase: m.purchaseCount ?? m.purchaseSessions,
        comparisonWebsiteSessions: comparisonMetrics.websiteSessions,
        comparisonPdpViews: comparisonMetrics.pdpViewSessions,
        comparisonAddToCart: comparisonMetrics.addToCartSessions,
        comparisonCheckout: comparisonMetrics.beginCheckoutSessions,
        comparisonPurchase:
          comparisonMetrics.purchaseCount ?? comparisonMetrics.purchaseSessions,
      },
      stageMetricsTable: [
        { key: "websiteSessions",                          label: "Website sessions",                     value: m.websiteSessions,                          comparisonValue: comparisonMetrics.websiteSessions,                          type: "number",  explanation: "Total sessions entering the website" },
        { key: "websiteToPdpViewRate",                     label: "Website to PDP view rate",             value: m.websiteToPdpViewRate != null ? m.websiteToPdpViewRate * 100 : null, comparisonValue: comparisonMetrics.websiteToPdpViewRate != null ? comparisonMetrics.websiteToPdpViewRate * 100 : null, type: "pct", explanation: "How many sessions reached a product page" },
        { key: "websiteToPdpDropOffSessions",              label: "Drop-off before PDP (sessions)",       value: m.websiteToPdpDropOffSessions,               comparisonValue: comparisonMetrics.websiteToPdpDropOffSessions,               type: "number",  explanation: "Sessions lost before product consideration" },
        { key: "websiteToPdpDropOffRate",                  label: "Drop-off before PDP (rate)",           value: m.websiteToPdpDropOffRate != null ? m.websiteToPdpDropOffRate * 100 : null, comparisonValue: comparisonMetrics.websiteToPdpDropOffRate != null ? comparisonMetrics.websiteToPdpDropOffRate * 100 : null, type: "pct", explanation: "Share lost before product pages" },
        { key: "pdpViewSessions",                          label: "PDP view sessions",                    value: m.pdpViewSessions,                          comparisonValue: comparisonMetrics.pdpViewSessions,                          type: "number",  explanation: "Sessions that viewed product detail pages" },
        { key: "pdpViewToAddToCartRate",                   label: "PDP to Add to Cart rate",              value: m.pdpViewToAddToCartRate != null ? m.pdpViewToAddToCartRate * 100 : null, comparisonValue: comparisonMetrics.pdpViewToAddToCartRate != null ? comparisonMetrics.pdpViewToAddToCartRate * 100 : null, type: "pct", explanation: "Whether PDPs persuade users to act" },
        { key: "pdpToAddToCartDropOffSessions",            label: "Drop-off at PDP (sessions)",           value: m.pdpToAddToCartDropOffSessions,             comparisonValue: comparisonMetrics.pdpToAddToCartDropOffSessions,             type: "number",  explanation: "PDP sessions that did not add to cart" },
        { key: "pdpToAddToCartDropOffRate",                label: "Drop-off at PDP (rate)",               value: m.pdpToAddToCartDropOffRate != null ? m.pdpToAddToCartDropOffRate * 100 : null, comparisonValue: comparisonMetrics.pdpToAddToCartDropOffRate != null ? comparisonMetrics.pdpToAddToCartDropOffRate * 100 : null, type: "pct", explanation: "Share of PDP sessions lost before cart" },
        { key: "addToCartSessions",                        label: "Add to Cart sessions",                 value: m.addToCartSessions,                        comparisonValue: comparisonMetrics.addToCartSessions,                        type: "number",  explanation: "Sessions that created cart intent" },
        { key: "addToCartToBeginCheckoutRate",             label: "ATC to Checkout rate",                 value: m.addToCartToBeginCheckoutRate != null ? m.addToCartToBeginCheckoutRate * 100 : null, comparisonValue: comparisonMetrics.addToCartToBeginCheckoutRate != null ? comparisonMetrics.addToCartToBeginCheckoutRate * 100 : null, type: "pct", explanation: "Whether cart sessions progress to checkout" },
        { key: "addToCartToBeginCheckoutDropOffSessions",  label: "Drop-off before checkout (sessions)",  value: m.addToCartToBeginCheckoutDropOffSessions,  comparisonValue: comparisonMetrics.addToCartToBeginCheckoutDropOffSessions,  type: "number",  explanation: "Cart sessions that did not start checkout" },
        { key: "addToCartToBeginCheckoutDropOffRate",      label: "Drop-off before checkout (rate)",      value: m.addToCartToBeginCheckoutDropOffRate != null ? m.addToCartToBeginCheckoutDropOffRate * 100 : null, comparisonValue: comparisonMetrics.addToCartToBeginCheckoutDropOffRate != null ? comparisonMetrics.addToCartToBeginCheckoutDropOffRate * 100 : null, type: "pct", explanation: "Share of cart sessions lost before checkout" },
        { key: "beginCheckoutSessions",                    label: "Begin checkout sessions",              value: m.beginCheckoutSessions,                    comparisonValue: comparisonMetrics.beginCheckoutSessions,                    type: "number",  explanation: "Sessions that started checkout" },
        { key: "beginCheckoutToPurchaseRate",              label: "Checkout to Purchase rate",            value: rawGa4Metrics.beginCheckoutToPurchaseRate != null ? rawGa4Metrics.beginCheckoutToPurchaseRate * 100 : null, comparisonValue: comparisonRawGa4Metrics.beginCheckoutToPurchaseRate != null ? comparisonRawGa4Metrics.beginCheckoutToPurchaseRate * 100 : null, type: "pct", explanation: "Whether checkout sessions complete" },
        { key: "beginCheckoutToPurchaseDropOffSessions",   label: "Drop-off during checkout (sessions)",  value: rawGa4Metrics.beginCheckoutToPurchaseDropOffSessions,   comparisonValue: comparisonRawGa4Metrics.beginCheckoutToPurchaseDropOffSessions,   type: "number",  explanation: "Sessions lost during checkout" },
        { key: "beginCheckoutToPurchaseDropOffRate",       label: "Drop-off during checkout (rate)",      value: rawGa4Metrics.beginCheckoutToPurchaseDropOffRate != null ? rawGa4Metrics.beginCheckoutToPurchaseDropOffRate * 100 : null, comparisonValue: comparisonRawGa4Metrics.beginCheckoutToPurchaseDropOffRate != null ? comparisonRawGa4Metrics.beginCheckoutToPurchaseDropOffRate * 100 : null, type: "pct", explanation: "Share of checkout sessions lost" },
        { key: "purchaseSessions",                         label: "Purchase sessions",                    value: rawGa4Metrics.purchaseSessions,                         comparisonValue: comparisonRawGa4Metrics.purchaseSessions,                         type: "number",  explanation: "Completed purchase sessions" },
        { key: "overallWebsiteToPurchaseRate",             label: "Overall website to purchase rate",     value: rawGa4Metrics.overallWebsiteToPurchaseRate != null ? rawGa4Metrics.overallWebsiteToPurchaseRate * 100 : null, comparisonValue: comparisonRawGa4Metrics.overallWebsiteToPurchaseRate != null ? comparisonRawGa4Metrics.overallWebsiteToPurchaseRate * 100 : null, type: "pct", explanation: "Full-funnel conversion" },
        { key: "purchaseCount",                            label: "Purchase count (orders)",              value: m.purchaseCount,                            comparisonValue: comparisonMetrics.purchaseCount,                            type: "number",  explanation: "Total completed orders" },
        { key: "purchaseRevenue",                          label: "Purchase revenue",                     value: m.purchaseRevenue,                          comparisonValue: comparisonMetrics.purchaseRevenue,                          type: "currency", explanation: "Total revenue generated" },
      ].filter((row) => row.value != null),
    };
  }, [activeWindowData, comparisonWindowData, comparisonLabel]);

  const storeFunnelData = useMemo(() => {
    const m =
      activeWindowData?.website_overall_conversion ||
      activeWindowData?.websiteConversion ||
      {};
    const wc =
      activeWindowData?.waterfall_chart ||
      activeWindowData?.waterfallChart ||
      {};
    const wt =
      activeWindowData?.weakest_transition ||
      activeWindowData?.weakestTransition ||
      null;
    const comparisonMetrics =
      comparisonWindowData?.website_overall_conversion ||
      comparisonWindowData?.websiteConversion ||
      {};
    const comparisonWaterfall =
      comparisonWindowData?.waterfall_chart ||
      comparisonWindowData?.waterfallChart ||
      {};
    const comparisonTransitions = comparisonWaterfall.transitions || [];
    const comparisonTransitionRateByKey = Object.fromEntries(
      comparisonTransitions.map((transition) => [
        `${transition.from}_${transition.to}`,
        transition.progressionRate != null
          ? transition.progressionRate * 100
          : null,
      ])
    );
    const validatedPurchaseCount = toFiniteNumber(m.purchaseCount, 0);
    const beginCheckoutSessions = toFiniteNumber(m.beginCheckoutSessions, 0);
    const displayWaterfall = {
      ...wc,
      stages: (Array.isArray(wc.stages) ? wc.stages : []).map((stage) =>
        stage?.key === "purchase_sessions"
          ? { ...stage, value: validatedPurchaseCount }
          : stage
      ),
      transitions: (Array.isArray(wc.transitions) ? wc.transitions : []).map((transition) => {
        if (transition?.to !== "purchase_sessions") return transition;
        const dropOffSessions = Math.max(
          beginCheckoutSessions - validatedPurchaseCount,
          0,
        );
        return {
          ...transition,
          progressionRate: beginCheckoutSessions
            ? validatedPurchaseCount / beginCheckoutSessions
            : 0,
          dropOffSessions,
          dropOffRate: beginCheckoutSessions
            ? dropOffSessions / beginCheckoutSessions
            : 0,
        };
      }),
    };

    const stageValueByKey = {};
    for (const s of displayWaterfall.stages || []) {
      if (s.key) stageValueByKey[s.key] = Number(s.value || 0);
    }

    const rawTransitions = displayWaterfall.transitions || [];
    const rates = rawTransitions
      .map((t) => t.progressionRate)
      .filter((r) => r != null);
    const minRate = rates.length ? Math.min(...rates) : null;

    const transitionLabel = (key) =>
      (key || "")
        .replace(/_sessions$/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const getAssessment = (rate, isWeakest) => {
      if (isWeakest) return { text: "Weakest stage", tone: "negative" };
      if (rate == null) return { text: "—", tone: "neutral" };
      if (rate >= 0.7) return { text: "Healthy", tone: "positive" };
      if (rate >= 0.5) return { text: "Above average", tone: "warning" };
      if (rate >= 0.3) return { text: "Normal", tone: "warning" };
      if (rate >= 0.1) return { text: "Below average", tone: "negative" };
      return { text: "Critical", tone: "negative" };
    };

    return {
      aiAssessment:
        activeWindowData?.narrative?.funnelAssessment?.headline || null,
      comparisonLabel,
      aiNextStep: Array.isArray(activeWindowData?.narrative?.whereToGoNext)
        ? activeWindowData.narrative.whereToGoNext.join(" • ")
        : null,
      summary: {
        orders: m.purchaseCount,
        revenue: m.purchaseRevenue,
        aov: m.averageOrderValue,
        conversionRate: m.websiteSessions
          ? (validatedPurchaseCount / toFiniteNumber(m.websiteSessions, 0)) * 100
          : null,
        comparisonOrders: comparisonMetrics.purchaseCount,
        comparisonRevenue: comparisonMetrics.purchaseRevenue,
        comparisonAov: comparisonMetrics.averageOrderValue,
        comparisonConversionRate: comparisonMetrics.overallWebsiteToPurchaseRate
          ? comparisonMetrics.overallWebsiteToPurchaseRate * 100
          : null,
      },
      waterfall: displayWaterfall,
      transitions: rawTransitions.map((t) => {
        const isWeakest = minRate != null && t.progressionRate === minRate;
        const assessment = getAssessment(t.progressionRate, isWeakest);
        return {
          key: `${t.from}_${t.to}`,
          label: `${transitionLabel(t.from)} → ${transitionLabel(t.to)}`,
          from: stageValueByKey[t.from] ?? null,
          to: stageValueByKey[t.to] ?? null,
          rate: t.progressionRate != null ? t.progressionRate * 100 : null,
          comparisonRate:
            comparisonTransitionRateByKey[`${t.from}_${t.to}`] ?? null,
          lost: t.dropOffSessions,
          assessment,
        };
      }),
      recommendedNextStep: wt
        ? {
            summary: `Weakest: ${wt.label} (${((wt.rate || 0) * 100).toFixed(1)}% conversion rate)`,
          }
        : null,
    };
  }, [activeWindowData, comparisonWindowData, comparisonLabel]);

  const pdpRankingsData = useMemo(() => {
    const r =
      activeWindowData?.pdp_rankings || activeWindowData?.pdpRankings || {};
    const withTrends = (rows) =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        attachComparisonTrends({ record: row, comparisonLookup })
      );
    return {
      ...r,
      topSelling: withTrends(r?.topSelling).filter(hasTopSellingSignal),
      highestContribution: withTrends(r?.highestContribution),
      mostOpportunity: withTrends(r?.mostOpportunity),
      bestConverting: withTrends(r?.bestConverting),
      losingMomentum: withTrends(r?.losingMomentum),
      aiOverview: activeWindowData?.narrative?.pdpRankingsOverview || null,
    };
  }, [activeWindowData, comparisonLookup]);

  const activeReportingPeriod = activeWindowData?.reporting_period || null;

  return (
    <div className={styles.container}>
      {typeof onOpenChannel === "function" ? (
        <div className={styles.windowBar}>
          <div className={styles.windowBarLeft}>
            <button
              type="button"
              className={styles.generateBtn}
              onClick={() =>
                onOpenChannel("pdp", {
                  label: "PDP",
                  type: "report",
                  key: `report:${report?._id || report?.id || "conversion"}`,
                  reportId: report?._id || report?.id || null,
                  collectionName: "conversion_and_pdp_report",
                  reportTitle: "Conversion & PDP",
                })
              }
            >
              Discuss in PDP channel
            </button>
          </div>
        </div>
      ) : null}

      {windowError && (
        <div className={styles.windowErrorBanner}>{windowError}</div>
      )}

      {windows.length > 0 && (
        <div className={styles.windowPickerRow}>
          <label
            htmlFor="conversion-pdp-window-select"
            className={styles.windowPickerLabel}
          >
            Time window
          </label>
          <select
            id="conversion-pdp-window-select"
            className={styles.windowPickerSelect}
            value={activeWindowKey || ""}
            onChange={(e) => handleWindowSelect(e.target.value)}
          >
            {windows.map((w) => (
              <option key={w.key} value={w.key}>
                {w.label}
              </option>
            ))}
          </select>
          {/* {activeReportingPeriod?.startDate && activeReportingPeriod?.endDate && (
            <p className={styles.windowPickerMeta}>
              {activeReportingPeriod.label
                ? `${activeReportingPeriod.label} · `
                : ""}
              {activeReportingPeriod.startDate} – {activeReportingPeriod.endDate}
            </p>
          )} */}
        </div>
      )}

      <MainTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className={styles.body}>
        {activeTab === "websiteConversion" && (
          <WebsiteConversionTab
            data={websiteConversionData}
            currency={shopCurrency}
          />
        )}

        {activeTab === "storeFunnel" && (
          <StoreFunnelTab
            data={storeFunnelData}
            onGoToRankings={() => setActiveTab("pdpRankings")}
            currency={shopCurrency}
          />
        )}

        {activeTab === "pdpRankings" && (
          <PdpRankingsTab
            data={pdpRankingsData}
            activeRankingTab={activeRankingTab}
            onRankingTabChange={setActiveRankingTab}
            onOpenProduct={openProduct}
            currency={shopCurrency}
            gscEnrichment={gscEnrichment}
          />
        )}

        {activeTab === "pdpDeepDive" && (
          <>
            <AnalysisFlowLog
              steps={pdpFlow.steps}
              label="PDP Deep Dive Analysis"
            />
            <PdpDeepDiveTab
              selectedProduct={selectedProduct}
              currency={shopCurrency}
              onAnalyze={handleAnalyzePdpDeepDive}
              isAnalyzing={isAnalyzingPdp}
              isCheckingAnalysis={isCheckingPdpAnalysis}
              analysisError={pdpAnalysisError}
              hasAnalysis={Boolean(fetchedPdpAnalysis)}
              seoData={fetchedSeoData || selectedProduct?.seoData || null}
              gscData={fetchedGscData || selectedProduct?.gscData || null}
            />
          </>
        )}
      </div>
    </div>
  );
}
